import { KeyboardEvent, useId, useState } from 'react';

/** TagEditor — editable chip list: existing tags render as removable pills, plus an add field. */
export interface TagEditorProps {
  label?: string;
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
}

/**
 * TagEditor — chip list for array fields (interests, style, dealbreakers).
 * Matches the read-only chip styling used elsewhere (pill, hairline border,
 * card fill) but each chip gets a remove affordance, plus a small input +
 * add button/Enter-to-add for new tags.
 */
export function TagEditor({ label, tags, onChange, placeholder = 'Add…' }: TagEditorProps) {
  const id = useId();
  const [draft, setDraft] = useState('');

  function commit() {
    const value = draft.trim();
    if (!value) return;
    if (!tags.some((t) => t.toLowerCase() === value.toLowerCase())) {
      onChange([...tags, value]);
    }
    setDraft('');
  }

  function remove(tag: string) {
    onChange(tags.filter((t) => t !== tag));
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && draft === '' && tags.length > 0) {
      remove(tags[tags.length - 1]);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label ? (
        <label htmlFor={id} style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-footnote)', fontWeight: 500, color: 'var(--text-soft)' }}>{label}</label>
      ) : null}
      <div
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center',
          background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 'var(--r-field)',
          padding: '8px 10px', minHeight: 42,
        }}
      >
        {tags.map((tag) => (
          <span
            key={tag}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-caption)', fontWeight: 500, color: 'var(--text-soft)',
              background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 999,
              padding: '5px 6px 5px 11px', whiteSpace: 'nowrap',
            }}
          >
            {tag}
            <button
              type="button"
              className="sk-hit"
              onClick={() => remove(tag)}
              aria-label={`Remove ${tag}`}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 16, height: 16, borderRadius: '50%', border: 'none', cursor: 'pointer',
                background: 'transparent', color: 'var(--text-dim)', padding: 0,
              }}
            >
              <i className="ti ti-x" style={{ fontSize: 'calc(12px * var(--text-scale))' }} />
            </button>
          </span>
        ))}
        <input
          id={id}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commit}
          placeholder={tags.length === 0 ? placeholder : ''}
          style={{
            flex: '1 1 90px', minWidth: 90, border: 'none', outline: 'none', background: 'transparent',
            fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-footnote)', color: 'var(--text)', padding: '4px 2px',
          }}
        />
      </div>
    </div>
  );
}

export default TagEditor;
